import React from 'react';
import { uniq } from 'lodash';
import { Divider, Grid, Typography } from '@mui/material';
import { useAppSelector } from '@/redux/hooks';
import { VersionGroupedByParent } from '@/redux/slices/slice_types';
import { selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { isTitleEntityV2 } from '@/_utils/utils';

interface OPVersionSelectionsFlatProps {
  selectedVersions: VersionGroupedByParent;
}

const OPVersionSelectionsFlat = ({ selectedVersions }: OPVersionSelectionsFlatProps) => {
  const versionsByKey = useAppSelector(selectVersionKeys);
  const versionKeys = Object.keys(selectedVersions);

  const selectVersionById = (id: string) => {
    const titleKey = String(id).substring(1);
    return versionsByKey[+titleKey] || {};
  };

  const renderVersionsListAsText = (versions: VersionGroupedByParent) => {
    // Check if this is actually a version object with feature parent
    if (versions && typeof versions === 'object') {
      // If the versions object has properties that suggest it's a single version object
      const hasVersionProperties = 'entity_type' in versions || 'lineage_meta' in versions || 'version_type' in versions;

      if (hasVersionProperties && versions.lineage_meta?.parent_entity_type === 'feature') {
        return null;
      }

      // Also check if any nested version has a feature parent
      const hasFeatureParent = Object.keys(versions).some((version) => {
        if (version.startsWith('_')) {
          const versionObj = selectVersionById(version);
          return versionObj.lineage_meta?.parent_entity_type === 'feature';
        }
        return false;
      });

      if (hasFeatureParent) {
        return null;
      }
    }

    const newSortedVersions = uniq(
      Object.keys(versions)
        .filter((key) => key.startsWith('_')) // Only process version IDs
        .map((version) => {
          const versionObj = selectVersionById(version);
          const isVersionV2 = isTitleEntityV2(versionObj);

          let epNo;
          if (versionObj.entity_type === 'version') {
            const versionParent = versionsByKey[isVersionV2 ? versionObj.relationships.parent?.id || 0 : versionObj?.parent || 0];
            if (!versionParent) return '?';
            epNo = !versionParent.sequence_order && versionParent.sequence_order !== 0 ? '?' : versionParent.sequence_order;
            return epNo;
          }

          epNo = isNaN(Number(versionObj.sequence_order)) ? versionObj.sequence_order || '?' : Number(versionObj.sequence_order);

          return epNo;
        })
    ).sort((a, b) => Number(a) - Number(b));

    // for creating the sequence range
    const ranges: string[] = [];
    let start, end;

    // Filter out non-numeric values before processing ranges
    const numericVersions = newSortedVersions.filter((v) => !isNaN(Number(v)) && Number(v) !== null && Number(v) !== undefined);

    for (let i = 0; i < numericVersions.length; i++) {
      start = Number(numericVersions[i]);
      end = start;
      while (i + 1 < numericVersions.length && Number(numericVersions[i + 1]) - Number(numericVersions[i]) == 1) {
        end = Number(numericVersions[i + 1]); // increment the index if the numbers sequential
        i++;
      }
      ranges.push(start == end ? start + '' : start + ' - ' + end);
    }

    // converting the range array to a string
    const versionsString =
      ranges.length > 0
        ? ranges.map((range) => `E${range}`).join(', ')
        : newSortedVersions
            .filter((v) => v !== null && v !== undefined)
            .map((v) => `E${v}`)
            .join(', '); // fallback for non-numeric episodes
    return <Typography sx={{ opacity: 0.6 }}>{versionsString}</Typography>;
  };

  return versionKeys
    .sort((a, b) => {
      const sequenceOrderA = Number(selectVersionById(a).sequence_order);
      const sequenceOrderB = Number(selectVersionById(b).sequence_order);
      if (isNaN(sequenceOrderA) || isNaN(sequenceOrderB)) return 0;
      return sequenceOrderA - sequenceOrderB;
    })
    .map((versionKey, index) => {
      const version = selectVersionById(versionKey);

      // Check if this version has a feature parent - if so, skip episode display
      const isFeatureContent = version.lineage_meta?.parent_entity_type === 'feature';

      // Get the rendered version text to check if it has content
      const versionText = !isFeatureContent ? renderVersionsListAsText(selectedVersions[versionKey]) : null;

      return (
        <Grid container key={versionKey} flexDirection="column">
          <Grid container alignItems="center">
            <Typography>{version.name}</Typography>
            {selectedVersions[versionKey] && versionText && <Typography sx={{ mx: 1, opacity: 0.6 }}>{'>'}</Typography>}
            {versionText}
          </Grid>
          {index !== versionKeys.length - 1 && (
            <Grid item>
              <Divider sx={{ my: 1, borderColor: 'action.selected', borderStyle: 'dashed' }} />
            </Grid>
          )}
        </Grid>
      );
    });
};

export default OPVersionSelectionsFlat;
